// Imperative toast — mirrors RTLS web's pattern. Mount <ToastHost />
// once near the root; call showToast(message, kind) from anywhere.

import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import clsx from 'clsx'

type ToastKind = 'info' | 'error' | 'success'

interface ToastEntry {
  id: number
  message: string
  kind: ToastKind
}

const listeners = new Set<(entry: ToastEntry) => void>()
let nextId = 1

export function showToast(message: string, kind: ToastKind = 'info'): void {
  const entry: ToastEntry = {id: nextId++, message, kind}
  listeners.forEach(l => l(entry))
}

const DISMISS_MS = 3500

export const ToastHost: React.FC = () => {
  const [entry, setEntry] = useState<ToastEntry | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onShow = (e: ToastEntry) => {
      setEntry(e)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setEntry(null), DISMISS_MS)
    }
    listeners.add(onShow)
    return () => {
      listeners.delete(onShow)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  if (!entry) return null

  return createPortal(
    <div
      className={clsx(
        'pointer-events-none fixed bottom-xxxl left-1/2 z-[100] -translate-x-1/2',
        'rounded-round px-lg py-md text-md font-medium shadow-lg',
        entry.kind === 'success' && 'bg-statusGreen text-white',
        entry.kind === 'error' && 'bg-statusRed text-white',
        entry.kind === 'info' && 'bg-surfaceLight text-textPrimary',
      )}
      role="status"
    >
      {entry.message}
    </div>,
    document.body,
  )
}
